import legalDocuments from '../../../legal_documents.json';

export type ConsentSource =
  | 'signup_email'
  | 'signup_google'
  | 'booking'
  | 'profile'
  | 'cookie_banner'
  | 'newsletter';

export type ConsentSelection = {
  termsAccepted: boolean;
  privacyAcknowledged: boolean;
  marketingEmailAccepted: boolean;
  analyticsCookiesAccepted: boolean;
  specialCategoryDataAccepted: boolean;
};

export type StoredConsentSummary = {
  acceptedAt?: string | null;
  termsVersion?: string | null;
  privacyVersion?: string | null;
  source?: ConsentSource | string | null;
  marketingEmailAccepted?: boolean;
  marketingConsentSource?: ConsentSource | string | null;
  marketingConsentEventId?: string | null;
  marketingConsentUpdatedAt?: string | null;
  marketingConsentLocale?: string | null;
  marketingConsentActorType?: string | null;
  analyticsCookiesAccepted?: boolean;
  analyticsConsentSource?: ConsentSource | string | null;
  analyticsConsentEventId?: string | null;
  analyticsConsentUpdatedAt?: string | null;
  specialCategoryDataAccepted?: boolean;
  lastEventId?: string | null;
  needsReacceptance?: boolean;
};

export type BookingLegalIntent =
  | 'explicit_new_acceptance'
  | 'reuse_existing_acceptance'
  | 'anonymous_user_acceptance';

export type BookingLegalContext = {
  intent: BookingLegalIntent;
  requiresSensitiveConsent: boolean;
  clientObservedNeedsReacceptance?: boolean;
};

export const LEGAL_DOCUMENTS = {
  terms: {
    ...legalDocuments.terms,
    path: '/cgv',
  },
  privacy: {
    ...legalDocuments.privacy,
    path: '/confidentialite',
  },
  cookies: {
    ...legalDocuments.cookies,
    path: '/cookies',
  },
} as const;

export function createEmptyConsentSelection(): ConsentSelection {
  return {
    termsAccepted: false,
    privacyAcknowledged: false,
    marketingEmailAccepted: false,
    analyticsCookiesAccepted: false,
    specialCategoryDataAccepted: false,
  };
}

export function needsReacceptance(summary?: StoredConsentSummary | null): boolean {
  if (!summary?.acceptedAt) {
    return true;
  }

  return (
    summary.termsVersion !== LEGAL_DOCUMENTS.terms.version ||
    summary.privacyVersion !== LEGAL_DOCUMENTS.privacy.version
  );
}

export function hasAcceptedCurrentMandatoryVersions(summary?: StoredConsentSummary | null): boolean {
  return !needsReacceptance(summary);
}

export function requiresSensitiveConsentForBooking(specialNeeds?: string | null): boolean {
  return Boolean(String(specialNeeds || '').trim());
}

export function resolveBookingLegalIntent({
  isAuthenticated,
  summary,
}: {
  isAuthenticated: boolean;
  summary?: StoredConsentSummary | null;
}): BookingLegalIntent {
  if (!isAuthenticated) {
    return 'anonymous_user_acceptance';
  }

  if (hasAcceptedCurrentMandatoryVersions(summary)) {
    return 'reuse_existing_acceptance';
  }

  return 'explicit_new_acceptance';
}

export function getCurrentLegalVersions() {
  return {
    termsVersion: LEGAL_DOCUMENTS.terms.version,
    privacyVersion: LEGAL_DOCUMENTS.privacy.version,
  };
}

export function getConsentErrorMessages(selection: ConsentSelection, options?: { requiresSpecialCategoryConsent?: boolean }) {
  const messages: string[] = [];

  if (!selection.termsAccepted) {
    messages.push("Vous devez accepter les Conditions Generales de Vente pour continuer.");
  }

  if (!selection.privacyAcknowledged) {
    messages.push("Vous devez confirmer avoir lu la Politique de confidentialite.");
  }

  if (options?.requiresSpecialCategoryConsent && !selection.specialCategoryDataAccepted) {
    messages.push("Vous devez autoriser le traitement des informations de sante/allergies que vous avez renseignees.");
  }

  return messages;
}